import type { EnvironmentType } from '@post-office/shared-contracts/execution-runtime';
import type { RequestContext } from '@post-office/shared-contracts/shared-request-middleware-context/requestContext';
import { getJiraOnboardingUrl } from '@post-office/message-templates-common--growth-starburst/utils/get-jira-onboarding-url';
import { type RedirectTo } from '@post-office/message-templates-common--growth-starburst/utils/resolve-redirect-to';

export {
	resolveRedirectTo,
	type RedirectTo,
} from '@post-office/message-templates-common--growth-starburst/utils/resolve-redirect-to';

const LOOM_ONBOARDING_URL_PROD = 'https://www.loom.com/meetings';
const LOOM_ONBOARDING_URL_NON_PROD = 'https://stage.loom.com/meetings';

// Unified Rovo create surface — tenant-agnostic, AI-first Confluence create experience.
const CONFLUENCE_CREATE_WITH_ROVO_URL = 'https://create.rovo.com';

export const getLoomOnboardingUrl = (environmentType: EnvironmentType): string => {
	return environmentType === 'Production' ? LOOM_ONBOARDING_URL_PROD : LOOM_ONBOARDING_URL_NON_PROD;
};

/**
 * Returns the Atlassian Home URL for the given environment. Shared between the
 * Jira and Confluence side-nav placements to avoid drift between previously
 * duplicated copies.
 */
export const getAtlassianHomeUrl = (env: EnvironmentType): string => {
	switch (env) {
		case 'Production':
			return 'https://home.atlassian.com';
		case 'Staging':
			return 'https://home.stg.atlassian.com';
		case 'Development':
			return 'http://localhost';
		default: {
			const _exhaustive: never = env;
			return _exhaustive;
		}
	}
};

type GetOnboardingUrlParams = {
	redirectTo: RedirectTo;
	requestContext: RequestContext;
	messageInstanceId: string;
	messageTemplateId: string;
	orgId?: string;
	twcEntitlementId?: string;
};

/**
 * Returns the onboarding URL for the personalized missing-app banner CTA.
 *
 * - Jira routes through the tenant-aware GUPS helper.
 * - Confluence routes to the tenant-agnostic Rovo Create surface.
 * - Loom is handled by callers via `getLoomOnboardingUrl` because
 * the destination depends only on environment, not on request context or flow.
 */
export const getOnboardingUrl = async ({
	redirectTo,
	requestContext,
	messageInstanceId,
	messageTemplateId,
	orgId,
	twcEntitlementId,
}: GetOnboardingUrlParams): Promise<string | undefined> => {
	switch (redirectTo) {
		case 'jira':
			return getJiraOnboardingUrl(
				requestContext,
				orgId,
				twcEntitlementId,
				messageInstanceId,
				messageTemplateId,
			);

		case 'confluence':
			return CONFLUENCE_CREATE_WITH_ROVO_URL;

		default: {
			const _exhaustive: never = redirectTo;
			return _exhaustive;
		}
	}
};
